import { AxiosResponse } from "axios";

import API from "../api";

import { Applications } from "../models/applications";
import { Branch } from "../models/branch";
import { Company } from "../models/company";
import { User } from "../models/user";
import { SupplierInvitePayload } from "../models/supplier";

import { Project } from "../models/project";
import { CustomerInvitePayload } from "../models/customer";

const ROUTE_NAME = "companies";

export type SignUp = {
  email: string;
  name: string;
  firstName?: string;
  lastName: string;
  phone: string;
  country?: string;
  subdomain?: string;
  companyName: string;
  companyInitials: string;
  areas?: Array<"sales" | "operations" | "warehousing">;
  videoSurveys?: "Yes" | "No";
  salesReps?: "1-10" | "11-20" | "Above 30";
  storage?: "Yes" | "No";
  warehouses?: "1-10" | "11-30" | "Above 30";
  shipmentTypes?: Array<"local" | "international" | "interstate" | "military">;
  shipmentVolume?: "1-4" | "5-600" | "601-10000" | "10001-20000" | "20001+";
  discover?: "website" | "email" | "google" | "newsletter" | "aiTools" | "referral" | "other";
  referralSource?: string;
  ownWords?: string;
};

const CompanyService = {
  async signUp(signUp?: SignUp): Promise<AxiosResponse<{ token?: string; user: User }>> {
    return await API.post(`/${ROUTE_NAME}/signUp`, signUp);
  },
  async count(options?: any): Promise<AxiosResponse<number>> {
    return await API.get(`/${ROUTE_NAME}/count`, { params: options });
  },
  async getById(id: number, options?: any): Promise<AxiosResponse<Company>> {
    return await API.get(`/${ROUTE_NAME}/${id}`, { params: options });
  },
  async getAll(options?: any): Promise<AxiosResponse<Company[]>> {
    return await API.get(`/${ROUTE_NAME}`, { params: options });
  },
  async create(company: Company): Promise<AxiosResponse<Company>> {
    return await API.post(`/${ROUTE_NAME}`, company);
  },
  async update(id: number, company: Company): Promise<AxiosResponse<boolean>> {
    return await API.put(`/${ROUTE_NAME}/${id}`, company);
  },
  async delete(id: number): Promise<AxiosResponse<any>> {
    return await API.delete(`/${ROUTE_NAME}/${id}`);
  },
  async approve(id: number): Promise<AxiosResponse<any>> {
    return await API.post(`/${ROUTE_NAME}/approve/${id}`);
  },

  async inviteUserInCompany(
    userEmail: string,
    companyId?: number,
    roleId?: number,
    typeUser?: string,
    projects?: Project[],
    code?: string,
    applications?: Applications[],
    branches?: Branch[]
  ): Promise<AxiosResponse<any>> {
    return await API.post(`/${ROUTE_NAME}/user/invite`, {
      email: userEmail,
      roleId,
      companyId,
      typeUser,
      projects,
      ...(code ? { code } : {}),
      ...(applications ? { applications } : {}),
      ...(branches ? { branches } : {}),
    });
  },

  async userAcceptInvite(userId: number, companyId: number): Promise<AxiosResponse<any>> {
    return await API.put(`/${ROUTE_NAME}/user/invite/accept`, { userId, companyId });
  },

  async userRejectInvite(userId: number, companyId: number): Promise<AxiosResponse<any>> {
    return await API.put(`/${ROUTE_NAME}/user/invite/reject`, { userId, companyId });
  },

  async userResendInvite(userId: number, companyId?: number): Promise<AxiosResponse<any>> {
    return await API.post(`/${ROUTE_NAME}/user/invite/resend`, { userId, companyId });
  },

  async userRevokeInvite(userId: number, companyId?: number) {
    return await API.put(`/${ROUTE_NAME}/user/invite/revoke`, { userId, companyId });
  },

  async inviteSupplierInCompany(payload: SupplierInvitePayload): Promise<AxiosResponse<any>> {
    return await API.post(`${ROUTE_NAME}/supplier/invite`, payload);
  },
  async inviteCustomerInCompany(payload: CustomerInvitePayload): Promise<AxiosResponse<any>> {
    return await API.post(`${ROUTE_NAME}/customer/invite`, payload);
  },

  async checkCompanyToken(params: { token: string }): Promise<AxiosResponse<{ exists: boolean; isActive: boolean }>> {
    return await API.get(`${ROUTE_NAME}/token/validate`, {
      params,
    })
  }
};

export default CompanyService;
